import axios, { AxiosInstance } from "axios";
import { Constants } from "../../utils/constants";
import logger from "../../utils/logger";
import { ApiResponse } from "../../types";
import { getCountry } from "country-currency-map";
import { UserLocation, MarketUnresolvedError } from "../../utils/locationUtils";
import { BROWSE_COUNTRY_KEY, getDynamicSupportedCountries } from "../../components/CountryPickerBanner";
import { getCurrencyForCountry } from "../../utils/countryCurrencyMap";
// Keep service layer decoupled from UI message handling.

// Types for Space Management API
export interface SpaceStatistics {
  totalSpaces: number;
  availableSpaces: number;
  bookedSpaces: number;
  availabilityPercentage: number;
  bookingRate: number;
  occupiedSpaces: number;
  occupancyRate: number;
}

// Types for Space Category
export interface SpaceCategory {
  id: number;
  name: string;
  description?: string;
}

// Types for Space Subcategory
export interface SpaceSubcategory {
  id: number;
  name: string;
  description?: string;
  categoryId?: number;
  category?: string;
}

// Types for creating a new space (updated to match new API payload)
export interface CreateSpacePayload {
  // Basic space information
  capacity: number;
  description: string;
  spaceType: "HIGH" | "MEDIUM" | "LOW";
  categoryId: number; // Updated: now uses categoryId instead of category string
  subCategoryId: number;
  size: string;

  // Pricing
  ratePrice: number;
  currency: string; // e.g., "EUR", "USD", "KES"
  duration: "HOURLY" | "DAILY" | "WEEKLY" | "MONTHLY";
  minimumDuration: number;
  maximumDuration: number;
  bufferTimeInMinutes: number;

  // Visibility and settings
  visibility: "PUBLIC" | "PRIVATE";
  zoneId: number; // NEW: Zone ID for location

  // Policy and Terms
  cancellationPolicyId?: number; // NEW: Link to cancellation policy
  termsIds?: number[]; // NEW: Array of terms & conditions IDs

  // Media structure (updated)
  media: {
    images: string[];
    videos: string[];
  };

  // Arrays
  addons: number[]; // Array of addon IDs
  amenityIds: number[]; // Amenity IDs

  // Availability
  availabilitySlots: AvailabilitySlot[];

  // Instances
  instances: SpaceInstancePayload[];
  numberOfInstances: number;

  // Event scheduling (NEW for PLAY services)
  eventSchedule?: EventSchedule;

  // Ticket type (NEW for PLAY services)
  ticketType?: string;
}

// Updated SpaceInstance interface for the new API
export interface SpaceInstancePayload {
  id?: number;
  name: string;
  floorId?: number; // NEW: Floor ID
  location: {
    name: string;
    buildingAddressId: number; // Updated: now uses buildingAddressId
  };
  availability: {
    day: string;
    startTime: {
      hour: number;
      minute: number;
      second: number;
      nano: number;
    };
    endTime: {
      hour: number;
      minute: number;
      second: number;
      nano: number;
    };
  };
}

// NEW: Event schedule interface for PLAY services
export interface EventSchedule {
  dayOfWeek: "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY";
  eventDate: string; // ISO date string
  recurrenceEndDate?: string; // ISO date string
  recurrenceType: "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
  recurring: boolean;
  startTime: {
    hour: number;
    minute: number;
    second: number;
    nano: number;
  };
  endTime: {
    hour: number;
    minute: number;
    second: number;
    nano: number;
  };
}

export interface AvailabilitySlot {
  day: "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY";
  startTime: {
    hour: number;
    minute: number;
    second: number;
    nano: number;
  };
  endTime: {
    hour: number;
    minute: number;
    second: number;
    nano: number;
  };
}

export interface FloorPlan {
  layout: FloorPlanElement[];
  timestamp: string; // ISO date string
}

export interface FloorPlanElement {
  elementType: string;
  h: number; // height
  w: number; // width
  x: number; // x position
  y: number; // y position
  i: string; // unique identifier
}

export interface Space {
  id: number;
  name: string;
  description?: string;
  propertyType: string;
  typeOfWorkspace: string;
  location: {
    id: number;
    address: string;
    city: string;
    country: string;
    latitude: string;
    longitude: string;
    streetName: string;
  };
  details: {
    id: number;
    capacity: number;
    floorNumber: string;
    propertySize: number;
    maxCapacity: number;
  };
  propertyPrice: {
    id: number;
    price: number;
    billingPeriod: string;
  };
  amenities: string[];
  tags: any[];
  zone: string;
  propertyStatus: string;
  listingType?: string;
  mediaId: number;
  userId: number;
  featured?: boolean;
  createdDate?: string;
  updatedDate?: string;
  media?: {
    id: number;
    title: string;
    description: string;
    images: Array<{
      id: number;
      url: string;
      filename: string;
    }>;
  };
}

export interface SpacesResponse {
  data: Space[];
  totalCount: number;
  pageSize: number;
  currentPage: number;
  totalPages: number;
}

export interface SpaceFilters {
  status?: string;
  propertyType?: string;
  typeOfWorkspace?: string;
  zone?: string;
  userId?: number;
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
}

// New interface for advanced space search
export interface SpaceSearchFilters {
  // Location filters
  addressContains?: string;
  buildingAddressId?: number;
  buildingName?: string;
  city?: string;
  country?: string;
  hostOrgName?: string;
  hostOrganizationId?: number;
  id?: number;

  // Space filters
  spaceName?: string;
  category?: "WORK" | "STAY" | "PLAY";
  categoryId?: number;
  spaceType?: "HIGH" | "MEDIUM" | "LOW";
  subCategoryId?: number;
  minCapacity?: number;
  status?: "PUBLISHED" | "UNPUBLISHED";
  featured?: boolean;
  duration?: ("HOURLY" | "DAILY" | "WEEKLY" | "MONTHLY")[];

  // Price filters
  minPrice?: number;
  maxPrice?: number;

  // Amenity filters
  amenityIds?: number[]; // Will be converted to comma-separated string

  // Subcategory-filter option selections (MOVE category). Array or pre-joined comma string.
  filterOptionIds?: number[] | string; // Will be converted to comma-separated string

  // Operating-territory selections (MOVE category). Array or pre-joined comma string.
  operatingTerritoryIds?: number[] | string; // Will be converted to comma-separated string

  // Availability filters (UTC ISO strings)
  specificDate?: string | string[]; // UTC ISO date string or array for multiple dates
  startDate?: string; // UTC ISO date string
  endDate?: string; // UTC ISO date string
  startTime?: string; // UTC time format
  endTime?: string; // UTC time format

  // Pagination parameters
  offset?: number;
  pageNumber?: number;
  pageSize?: number;
  paged?: boolean;
  unpaged?: boolean;

  // Sorting parameters
  sort?: string; // e.g., "createdAt,desc"
  "sort.sorted"?: boolean;
  "sort.unsorted"?: boolean;
  topSpaces?: boolean;
}

// Enhanced space interface for search results
export interface SearchSpaceResult {
  id: number;
  capacity: number;
  description: string;
  spaceType: "HIGH" | "MEDIUM" | "LOW";
  category: "WORK" | "STAY" | "PLAY";
  size: string;
  ratePrice: number;
  currency: string;
  minimumDuration: number;
  maximumDuration: number;
  bufferTimeInMinutes: number;
  status: "PUBLISHED" | "UNPUBLISHED";
  images: string[];
  availableInstancesCount: number;

  // Enhanced nested objects
  subCategory: {
    id: number;
    name: string;
    category: "WORK" | "STAY" | "PLAY";
  };

  amenities: Array<{
    id: number;
    name: string;
    description?: string;
    imageUrls?: string[];
  }>;

  addons: Array<{
    id: number;
    name: string;
    description?: string;
    unitPrice: number;
    type: "NON_SUBSCRIPTION" | "SUBSCRIPTION";
    serviceCategory: "WORK" | "STAY" | "PLAY";
    serviceType?: string;
    duration: string;
    maximumValidity?: number;
    images?: string[];
    applicableUserCategories?: string[];
    availabilitySlots?: AvailabilitySlot[];
    instances?: Array<{
      id: number;
      instanceNumber: number;
      createdAt: string;
      availabilitySlots?: AvailabilitySlot[];
    }>;
    createdAt: string;
    updatedAt: string;
  }>;

  instances: Array<{
    id: number;
    instanceNumber: number;
    title?: string;
    createdAt: string;
    location: {
      name: string;
      floorLevel: number;
      buildingAddress: {
        id: number;
        buildingName?: string;
        streetAddress?: string;
        city?: string;
        region?: string;
        country?: string;
        postalCode?: string;
        digitalAddress?: string;
        latitude?: number;
        longitude?: number;
        images?: string[];
        zone?: {
          id: number;
          name: string;
          description?: string;
          city?: string;
          country?: string;
          shape: "CIRCLE" | "POLYGON";
          radiusInMeters?: number;
          zoneColor?: string;
          perimeter?: Array<{
            latitude: number;
            longitude: number;
          }>;
        };
      };
    };
    availabilitySlots?: AvailabilitySlot[];
  }>;

  availableSlots: AvailabilitySlot[];

  zone?: {
    id: number;
    name: string;
    description?: string;
    city?: string;
    country?: string;
    shape: "CIRCLE" | "POLYGON";
    radiusInMeters?: number;
    zoneColor?: string;
    perimeter?: Array<{
      latitude: number;
      longitude: number;
    }>;
  };
}

export interface SpaceSearchResponse {
  content: SearchSpaceResult[];
  pageable: {
    sort: {
      unsorted: boolean;
      sorted: boolean;
      empty: boolean;
    };
    pageNumber: number;
    pageSize: number;
    offset: number;
    unpaged: boolean;
    paged: boolean;
  };
  totalPages: number;
  last: boolean;
  totalElements: number;
  sort: {
    unsorted: boolean;
    sorted: boolean;
    empty: boolean;
  };
  numberOfElements: number;
  first: boolean;
  size: number;
  number: number;
  empty: boolean;
}

// Types for Booking Management API
export interface Booking {
  id: number;
  spaceId: number;
  spaceInstanceId: number;
  emails: string[];
  startTime: string; // ISO date string
  endTime: string; // ISO date string
  notes?: string;
  createdAt: string;
  updatedAt?: string;
  status: "PENDING" | "CONFIRMED" | "CANCELLED" | "COMPLETED";
  addons?: Array<{
    addonId: number;
    name?: string;
    quantity: number;
    unitPrice?: number;
    totalPrice?: number;
    currency?: string;
  }>;
}

export interface BookingsResponse {
  content: Booking[];
  pageable: {
    sort: {
      unsorted: boolean;
      sorted: boolean;
      empty: boolean;
    };
    pageNumber: number;
    pageSize: number;
    offset: number;
    unpaged: boolean;
    paged: boolean;
  };
  totalPages: number;
  last: boolean;
  totalElements: number;
  sort: {
    unsorted: boolean;
    sorted: boolean;
    empty: boolean;
  };
  numberOfElements: number;
  first: boolean;
  size: number;
  number: number;
  empty: boolean;
}

export interface CreateBookingPayload {
  userId: string; // Changed to string as per API spec
  spaceId: number; // Space ID (not workspaceId)
  emails: string[]; // Array of email addresses to notify
  startTime: string; // Pure UTC ISO date string (no offset)
  endTime: string; // Pure UTC ISO date string (no offset)
  notes?: string;
  teamId?: number;
  addons?: Array<{
    addonId: number;
    quantity: number;
  }>;
  // Optional pickup meetup point (MOVE/rental bookings). All sub-fields are strings and
  // optional; omit to fall back to the fleet depot. Relayed verbatim to Maestro.
  pickup?: {
    label: string;
    latitude: string;
    longitude: string;
    placeId: string;
    digitalAddress: string;
  };
}

export interface UpdateBookingPayload {
  emails?: string[]; // Array of email addresses to notify
  newStartTime?: string; // Pure UTC ISO date string (no offset)
  newEndTime?: string; // Pure UTC ISO date string (no offset)
  notes?: string;
}

export interface BookingFilters {
  // Basic filters
  userId?: number | string;
  spaceId?: number;
  spaceInstanceId?: number;
  status?: string;

  // Date/Time filters (UTC ISO strings)
  startDate?: string;
  endDate?: string;
  startTimeFrom?: string;
  startTimeTo?: string;
  endTimeFrom?: string;
  endTimeTo?: string;

  // Location filters
  addressContains?: string;
  city?: string;
  buildingName?: string;

  // Space filters
  spaceName?: string;
  subCategoryId?: number;
  amenityIds?: string; // Comma-separated list

  // Booking owner filters
  bookingOwnerEmail?: string;
  bookingOwnerName?: string;

  // Host filters
  hostOrgName?: string;

  // Pagination and sorting
  pageNumber?: number;
  pageSize?: number;
  page?: number;
  size?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: string;
  sort?: string;
  paged?: boolean;
  unpaged?: boolean;
  "sort.sorted"?: boolean;
  "sort.unsorted"?: boolean;

  // General search parameter
  search?: string;
}

class SpaceManagementService {
  private api: AxiosInstance;

  /**
   * Get user profile from localStorage
   */
  private getUserProfile(): any {
    try {
      const userStr = localStorage.getItem("user");
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      logger.error("Error parsing user profile:", error);
      return null;
    }
  }

  /**
   * Get cached location from localStorage
   */
  private getCachedLocation(): UserLocation | null {
    try {
      const locationStr = localStorage.getItem("userLocation");
      if (locationStr) {
        const location = JSON.parse(locationStr);
        // Check if location is recent (within 6 hours)
        if (location.timestamp && Date.now() - location.timestamp < 6 * 60 * 60 * 1000) {
          return location;
        }
      }
    } catch (error) {
      logger.error("Error parsing cached location:", error);
    }
    return null;
  }

  /**
   * Get currency code from country code using country-currency-map package
   */
  private getCurrencyFromCountry(countryCode: string): string {
    try {
      const countryData = getCountry(countryCode);
      return countryData?.currency || "KES";
    } catch (error) {
      logger.error("Error mapping country to currency:", error);
      return "KES";
    }
  }

  /**
   * Add context headers to API requests
   *
   * Fallback Priority Order for Each Header:
   *
   * X-Country-Code:
   *   1. User-selected browse country (userBrowseCountry) — set by CountryPickerBanner
   *   2. Cached GPS location (countryCode) if it is an operating country
   *   3. OMITTED when none of the above resolve
   *
   * There is deliberately no hardcoded default market. A visitor whose
   * location is unknown, denied, or outside our operating countries is sent
   * to the country picker (see detectUnsupportedCountry) instead of silently
   * being served another market's spaces. Mirrors mobile's InitialSearchScreen.
   *
   * X-City:
   *   1. Browse country default city
   *   2. Cached GPS location (city)
   *   3. OMITTED when the country is unknown
   *
   * X-Currency-Code:
   *   1. localStorage (userCurrency) - User's explicit choice
   *   2. Currency of the resolved country
   *   3. OMITTED when no country is resolved
   *
   * @returns true when an operating country was resolved. When false, no
   *   country context was attached and the caller MUST NOT treat the response
   *   as a valid market search.
   */
  private addContextHeaders(config: any): boolean {
    const cachedLocation = this.getCachedLocation();
    const user = this.getUserProfile();
    const isPlatformAdmin = user?.organization?.type?.code === "PLATFORM_ADMIN";

    // Get Timezone Name first as it's the primary fallback
    let timezoneName = "Africa/Nairobi";
    try {
      timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone || "Africa/Nairobi";
      config.headers["X-Timezone-Name"] = timezoneName;
    } catch (error) {
      config.headers["X-Timezone-Name"] = "Africa/Nairobi";
    }

    // X-Timezone-Offset: Always from Browser (in minutes, e.g., 180 for UTC+3)
    try {
      const offset = new Date().getTimezoneOffset() * -1;
      config.headers["X-Timezone-Offset"] = String(offset);
    } catch (error) {
      config.headers["X-Timezone-Offset"] = "180"; // Default to EAT (UTC+3)
    }

    // X-Locale: Always from Browser
    config.headers["X-Locale"] = navigator.language || "en-KE";

    // If platform admin, skip location headers
    if (isPlatformAdmin) {
      return true;
    }

    // X-Country-Code Priority:
    //  1. User-selected browse country (userBrowseCountry) — set when a visitor
    //     picks a market from the CountryPickerBanner. Mirrors mobile's
    //     _browseByCountryMode, which prevents 0-results for visitors outside
    //     our operating countries.
    //  2. Cached GPS location (countryCode), if it is an operating country
    //  3. Nothing — header omitted, and the UI shows the country picker.
    //
    // The browser location prompt is the only location signal. There is no
    // timezone inference and no IP lookup, so we never guess a market.
    let browseCountryCode: string | null = null;
    let browseCity: string | null = null;
    try {
      const saved = localStorage.getItem(BROWSE_COUNTRY_KEY);
      if (saved) {
        const match = getDynamicSupportedCountries().find(c => c.code === saved);
        if (match) {
          browseCountryCode = match.code;
          browseCity        = match.defaultCity || null;
        }
      }
    } catch {
      // localStorage may be blocked in some contexts
    }

    const supportedCodes = new Set(getDynamicSupportedCountries().map(c => c.code));
    const isSupported = (code?: string | null) => !!code && supportedCodes.has(code.toUpperCase() as any);

    const countryCode =
      browseCountryCode ||
      (cachedLocation?.countryCode && isSupported(cachedLocation.countryCode) ? cachedLocation.countryCode : null) ||
      null;

    // Unknown market: send no country context at all rather than defaulting to
    // one market's inventory. The country picker is what surfaces here.
    if (countryCode) {
      config.headers["X-Country-Code"] = countryCode;

      // X-City Priority: BrowseCountry → Cached GPS → Country default
      const matchCountry = getDynamicSupportedCountries().find(c => c.code?.toUpperCase() === countryCode?.toUpperCase());
      config.headers["X-City"] =
        browseCity ||
        cachedLocation?.city ||
        matchCountry?.defaultCity ||
        (countryCode === "GH" ? "Accra" : "Nairobi");

      // X-Currency-Code Priority: localStorage → resolved country's currency
      const userCurrencyAttr = localStorage.getItem("userCurrency");
      const explicitCurrency =
        userCurrencyAttr && userCurrencyAttr !== "null" && userCurrencyAttr !== "undefined" ? userCurrencyAttr : null;

      const defaultCurrency = matchCountry?.currency || getCurrencyForCountry(countryCode);
      config.headers["X-Currency-Code"] = explicitCurrency || defaultCurrency;
    } else {
      // Clear any value left over from a previous request on this instance.
      delete config.headers["X-Country-Code"];
      delete config.headers["X-City"];
      delete config.headers["X-Currency-Code"];
      logger.debug(
        "No operating country resolved (no browse selection or GPS) — omitting country headers; picker expected"
      );
    }

    // X-Latitude & X-Longitude: Optional - only if available from cached location
    if (cachedLocation?.latitude && cachedLocation?.longitude) {
      config.headers["X-Latitude"] = String(cachedLocation.latitude);
      config.headers["X-Longitude"] = String(cachedLocation.longitude);
    }

    return !!countryCode;
  }

  /**
   * Block a market-scoped request when we do not know which country the visitor
   * is browsing.
   *
   * Sending the request without a country would let the backend fall back to a
   * default region, so the visitor would see spaces from a market they never
   * chose. We refuse the call instead and let the UI open the country picker.
   */
  private assertMarketResolved(config: any): void {
    if (!this.addContextHeaders(config)) {
      throw new MarketUnresolvedError(
        "Search blocked: no operating country resolved. Show the country picker."
      );
    }
  }

  constructor() {
    // Create authenticated API instance with booking base URL
    this.api = axios.create({
      baseURL: Constants.SPACE_MANAGEMENT_API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add auth token interceptor with fallback like apiV2Service
    this.api.interceptors.request.use(config => {
      let token = localStorage.getItem("authToken");
      if (!token) {
        token = localStorage.getItem("access_token");
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add context headers for backend
      this.addContextHeaders(config);

      return config;
    });

    // Add response interceptor
    this.api.interceptors.response.use(
      response => {
        // Handle 204 No Content specifically
        if (response.status === 204) {
          return {
            data: null,
            status: 204,
            message: "Success",
          };
        }

        return {
          data: response.data,
          status: response.status,
          message: response.data?.message,
        };
      },
      error => {
        logger.error("SpaceManagementService API Error:", error);
        throw error;
      }
    );
  }

  /**
   * Get supported countries list from booking service
   * GET /api/v2/spaces/countries
   * Fetches fresh list from API every time without caching.
   */
  public async getSupportedCountries(): Promise<ApiResponse<Array<{
    filterValue: string;
    code: string;
    name: string;
    flag: string;
    flagUrl: string | null;
    currency: string;
    callingCode: string;
  }>>> {
    try {
      const response = await this.api.get("/api/v2/spaces/countries");
      return response;
    } catch (error) {
      logger.error("Get Supported Countries Error:", error);
      throw error;
    }
  }

  public async getSpaceStatistics(_userId?: number): Promise<ApiResponse<SpaceStatistics>> {
    try {
      logger.log("Getting space statistics");

      // Don't pass userId as query parameter - API should determine user from auth token
      const response = await this.api.get("/api/v2/spaces/statistics");

      logger.log("Get space statistics response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting space statistics:", error);
      throw error;
    }
  }

  public async getSpaces(filters: SpaceFilters = {}): Promise<ApiResponse<SpacesResponse>> {
    try {
      logger.log("Getting spaces with filters:", filters);

      const params = new URLSearchParams();

      // Add filters as query parameters
      if (filters.status) params.append("status", filters.status);
      if (filters.propertyType) params.append("propertyType", filters.propertyType);
      if (filters.typeOfWorkspace) params.append("typeOfWorkspace", filters.typeOfWorkspace);
      if (filters.zone) params.append("zone", filters.zone);
      // Removed userId parameter - API should determine user from auth token
      if (filters.pageNumber !== undefined) params.append("page", filters.pageNumber.toString());
      if (filters.pageSize) params.append("size", filters.pageSize.toString());
      if (filters.search) params.append("search", filters.search);
      if (filters.sortBy) params.append("sortBy", filters.sortBy);
      if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);

      const response = await this.api.get(`/api/v2/spaces?${params.toString()}`);

      logger.log("Get spaces response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting spaces:", error);
      throw error;
    }
  }

  public async searchSpaces(
    filters: SpaceSearchFilters = {},
    timezone?: number
  ): Promise<ApiResponse<SpaceSearchResponse>> {
    try {
      logger.log("Searching spaces with filters:", filters);

      const params = new URLSearchParams();

      // Location filters
      if (filters.addressContains) params.append("addressContains", filters.addressContains);
      if (filters.buildingAddressId) params.append("buildingAddressId", filters.buildingAddressId.toString());
      if (filters.buildingName) params.append("buildingName", filters.buildingName);
      if (filters.city) params.append("city", filters.city);
      if (filters.country) params.append("country", filters.country);
      if (filters.hostOrgName) params.append("hostOrgName", filters.hostOrgName);
      if (filters.hostOrganizationId) params.append("hostOrganizationId", filters.hostOrganizationId.toString());
      if (filters.id) params.append("id", filters.id.toString());

      // Space filters
      if (filters.spaceName) params.append("spaceName", filters.spaceName);
      if (filters.category) params.append("category", filters.category);
      if (filters.categoryId) params.append("categoryId", filters.categoryId.toString());
      if (filters.spaceType) params.append("spaceType", filters.spaceType);
      if (filters.subCategoryId) params.append("subCategoryId", filters.subCategoryId.toString());
      if (filters.minCapacity) params.append("minCapacity", filters.minCapacity.toString());
      if (filters.status) params.append("status", filters.status);
      if (filters.featured !== undefined) params.append("featured", filters.featured.toString());

      // Duration filters (convert array to comma-separated string)
      if (filters.duration && filters.duration.length > 0) {
        params.append("duration", filters.duration.join(","));
      }

      // Price filters
      if (filters.minPrice !== undefined) params.append("minPrice", filters.minPrice.toString());
      if (filters.maxPrice !== undefined) params.append("maxPrice", filters.maxPrice.toString());

      // Amenity filters (convert array to comma-separated string or use as-is if already a string)
      if (filters.amenityIds && filters.amenityIds.length > 0) {
        // Handle both array and string formats
        const amenityIdsValue = Array.isArray(filters.amenityIds)
          ? filters.amenityIds.join(",")
          : String(filters.amenityIds);
        params.append("amenityIds", amenityIdsValue);
      }

      // Subcategory-filter option selections (convert array to comma-separated string)
      if (filters.filterOptionIds && filters.filterOptionIds.length > 0) {
        const filterOptionIdsValue = Array.isArray(filters.filterOptionIds)
          ? filters.filterOptionIds.join(",")
          : String(filters.filterOptionIds);
        params.append("filterOptionIds", filterOptionIdsValue);
      }

      // Operating-territory selections (MOVE; convert array to comma-separated string)
      if (filters.operatingTerritoryIds && filters.operatingTerritoryIds.length > 0) {
        const operatingTerritoryIdsValue = Array.isArray(filters.operatingTerritoryIds)
          ? filters.operatingTerritoryIds.join(",")
          : String(filters.operatingTerritoryIds);
        params.append("operatingTerritoryIds", operatingTerritoryIdsValue);
      }

      if (filters.topSpaces !== undefined) {
        params.append("topSpaces", filters.topSpaces.toString());
      }

      // Availability filters - Convert ISO strings to date-only format (YYYY-MM-DD)
      if (filters.specificDate) {
        // Handle multiple specific dates by adding multiple specificDate params
        const specificDates = Array.isArray(filters.specificDate) ? filters.specificDate : [filters.specificDate];
        specificDates.forEach(date => {
          const dateOnly = new Date(date).toISOString().split("T")[0];
          params.append("specificDate", dateOnly);
        });
      }

      if (filters.startDate) {
        const dateOnly = new Date(filters.startDate).toISOString().split("T")[0];
        params.append("startDate", dateOnly);
      }

      if (filters.endDate) {
        const dateOnly = new Date(filters.endDate).toISOString().split("T")[0];
        params.append("endDate", dateOnly);
      }

      if (filters.startTime) params.append("startTime", filters.startTime);
      if (filters.endTime) params.append("endTime", filters.endTime);

      // Timezone offset for context
      if (timezone !== undefined) params.append("timezone", timezone.toString());

      // Pagination parameters - Map to correct API parameter names
      if (filters.offset !== undefined) params.append("offset", filters.offset.toString());
      if (filters.pageNumber !== undefined) params.append("page", filters.pageNumber.toString());
      if (filters.pageSize !== undefined) params.append("size", filters.pageSize.toString());
      if (filters.paged !== undefined) params.append("paged", filters.paged.toString());
      if (filters.unpaged !== undefined) params.append("unpaged", filters.unpaged.toString());

      // Sorting parameters
      if (filters.sort) params.append("sort", filters.sort);
      if (filters["sort.sorted"] !== undefined) params.append("sort.sorted", filters["sort.sorted"].toString());
      if (filters["sort.unsorted"] !== undefined) params.append("sort.unsorted", filters["sort.unsorted"].toString());

      const response = await this.api.get(`/api/v2/spaces/search?${params.toString()}`);

      logger.log("Search spaces response:", response);
      return response;
    } catch (error) {
      logger.error("Error searching spaces:", error);
      throw error;
    }
  }

  public async getUserSpaces(
    userId: number,
    filters: Omit<SpaceFilters, "userId"> = {}
  ): Promise<ApiResponse<SpacesResponse>> {
    // Don't pass userId to getSpaces - API should determine user from auth token
    return this.getSpaces(filters);
  }

  public async getSpaceById(id: number, _userId?: string): Promise<ApiResponse<any>> {
    try {
      logger.log("Getting space by id:", id);
      // Don't pass userId as query parameter - API should determine user from auth token
      const response = await this.api.get(`/api/v2/spaces/${id}`);
      logger.log("Get space by id response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting space by id:", error);
      throw error;
    }
  }

  public async getPublicSpaceById(id: number): Promise<ApiResponse<any>> {
    return this.getSpaceById(id);
  }

  /**
   * Helper function to convert time string (HH:MM:SS) to time object
   */
  private convertTimeStringToObject(timeString: string) {
    const [hours, minutes, seconds] = timeString.split(":").map(Number);
    return {
      hour: hours || 0,
      minute: minutes || 0,
      second: seconds || 0,
      nano: 0,
    };
  }

  /**
   * Helper: Convert time object {hour, minute, second} to string HH:MM:SS
   */
  private convertTimeObjectToString(timeObj: any): string {
    if (!timeObj || typeof timeObj !== "object") return "00:00:00";
    const h = String(timeObj.hour ?? 0).padStart(2, "0");
    const m = String(timeObj.minute ?? 0).padStart(2, "0");
    const s = String(timeObj.second ?? 0).padStart(2, "0");
    return `${h}:${m}:${s}`;
  }

  /**
   * Helper: Ensure a time value is a string in HH:MM:SS
   */
  private ensureTimeString(value: any): string {
    if (typeof value === "string") {
      const parts = value.split(":");
      if (parts.length === 2) {
        const [h, m] = parts;
        return `${h.padStart(2, "0")}:${m.padStart(2, "0")}:00`;
      }
      if (parts.length === 3) {
        const [h, m, s] = parts;
        return `${h.padStart(2, "0")}:${m.padStart(2, "0")}:${s.padStart(2, "0")}`;
      }
      return "00:00:00";
    }
    return this.convertTimeObjectToString(value);
  }

  /**
   * Normalize availability slot times to HH:MM:SS strings
   */
  private normalizeAvailabilitySlotsToStrings(slots: any[]): any[] {
    return (slots || []).map(slot => ({
      day: slot.day,
      startTime: this.ensureTimeString(slot.startTime),
      endTime: this.ensureTimeString(slot.endTime),
    }));
  }

  public async createSpace(payload: CreateSpacePayload, timezone?: number): Promise<ApiResponse> {
    try {
      logger.log("Creating space with payload:", payload);

      // Normalize all time fields to HH:MM:SS strings before sending
      const processedPayload: any = {
        ...payload,
        availabilitySlots: this.normalizeAvailabilitySlotsToStrings(payload.availabilitySlots || []),
        instances: payload.instances.map(instance => ({
          ...instance,
          // Only process availability if it exists
          ...(instance.availability && {
            availability: {
              ...instance.availability,
              startTime: this.ensureTimeString(instance.availability.startTime),
              endTime: this.ensureTimeString(instance.availability.endTime),
            },
          }),
        })),
        // Ensure eventSchedule times are in correct format if present
        eventSchedule: payload.eventSchedule
          ? {
              ...payload.eventSchedule,
              startTime: this.ensureTimeString(payload.eventSchedule.startTime),
              endTime: this.ensureTimeString(payload.eventSchedule.endTime),
            }
          : undefined,
      };

      // Add timezone offset if provided and eventSchedule exists
      if (timezone !== undefined && processedPayload.eventSchedule) {
        processedPayload.timezone = timezone;
      }

      const response = await this.api.post("/api/v2/spaces", processedPayload);

      logger.log("Create space response:", response);
      return response;
    } catch (error) {
      logger.error("Error creating space:", error);
      throw error;
    }
  }

  public async updateSpace(spaceId: number, payload: any, _userId?: number): Promise<ApiResponse> {
    try {
      logger.log("Updating space with id:", spaceId, "payload:", payload);

      // Don't pass userId as query parameter - API should determine user from auth token
      const response = await this.api.patch(`/api/v2/spaces/${spaceId}`, payload);

      logger.log("Update space response:", response);
      return response;
    } catch (error) {
      logger.error("Error updating space:", error);
      throw error;
    }
  }

  public async deleteSpace(id: number, _userId: number): Promise<ApiResponse> {
    try {
      logger.log("Deleting space with id:", id);

      // Don't pass userId as query parameter - API should determine user from auth token
      const response = await this.api.delete(`/api/v2/spaces/${id}`);

      logger.log("Delete space response:", response);
      return response;
    } catch (error) {
      logger.error("Error deleting space:", error);
      throw error;
    }
  }

  public async publishSpace(id: number, _userId: number): Promise<ApiResponse> {
    try {
      logger.log("Publishing space with id:", id);
      const params = new URLSearchParams();
      params.append("status", "PUBLISHED");
      // Don't pass userId as query parameter - API should determine user from auth token
      const response = await this.api.put(`/api/v2/spaces/${id}?${params.toString()}`);
      logger.log("Publish space response:", response);
      return response;
    } catch (error) {
      logger.error("Error publishing space:", error);
      throw error;
    }
  }

  public async unpublishSpace(id: number, _userId: number): Promise<ApiResponse> {
    try {
      logger.log("Unpublishing space with id:", id);
      const params = new URLSearchParams();
      params.append("status", "UNPUBLISHED");
      // Don't pass userId as query parameter - API should determine user from auth token
      const response = await this.api.put(`/api/v2/spaces/${id}?${params.toString()}`);
      logger.log("Unpublish space response:", response);
      return response;
    } catch (error) {
      logger.error("Error unpublishing space:", error);
      throw error;
    }
  }

  public async toggleFeaturedSpace(id: number, featured: boolean): Promise<ApiResponse> {
    try {
      logger.log("Toggling featured status for space with id:", id, "featured:", featured);

      const params = new URLSearchParams();
      params.append("value", featured.toString());

      const response = await this.api.patch(`/api/v2/spaces/${id}/featured?${params.toString()}`);
      logger.log("Toggle featured space response:", response);
      return response;
    } catch (error) {
      logger.error("Error toggling featured space:", error);
      throw error;
    }
  }

  public async uploadMedia(files: File[]): Promise<ApiResponse<string[]>> {
    try {
      const formData = new FormData();

      // Add files to FormData
      files.forEach(file => {
        formData.append("files", file);
      });

      logger.log(`Uploading ${files.length} media files`);

      // Use the same API instance but override Content-Type for multipart/form-data
      const response = await this.api.post("/api/v2/media/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      logger.log("Media upload response:", response);
      return response;
    } catch (error) {
      logger.error("Error uploading media:", error);
      throw error;
    }
  }

  public async getSpaceCategories(): Promise<ApiResponse<SpaceCategory[]>> {
    try {
      logger.log("Getting space categories");

      const response = await this.api.get("/api/v2/spaces/categories");

      logger.log("Get space categories response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting space categories:", error);
      throw error;
    }
  }

  public async searchPublicSpaces(params?: {
    addressContains?: string;
    amenityIds?: string;
    buildingAddressId?: number;
    buildingName?: string;
    category?: string;
    city?: string;
    country?: string;
    endDate?: string;
    endTime?: string;
    featured?: boolean;
    hostOrgName?: string;
    hostOrganizationId?: number;
    maxPrice?: number;
    minCapacity?: number;
    minPrice?: number;
    offset?: number;
    pageNumber?: number;
    pageSize?: number;
    paged?: boolean;
    "sort.sorted"?: boolean;
    "sort.unsorted"?: boolean;
    spaceName?: string;
    specificDate?: string | string[];
    startDate?: string;
    startTime?: string;
    status?: "PUBLISHED" | "UNPUBLISHED";
    subCategoryId?: number;
    timezone?: number;
    topSpaces?: boolean;
    unpaged?: boolean;
  }): Promise<ApiResponse<SpaceSearchResponse>> {
    try {
      logger.log("Searching public spaces with params:", params);

      // Process date parameters to convert to date-only format
      const apiParams: any = { ...params };

      // Convert dates to date-only format (YYYY-MM-DD)
      if (apiParams.specificDate) {
        const specificDates = Array.isArray(apiParams.specificDate) ? apiParams.specificDate : [apiParams.specificDate];
        apiParams.specificDate = specificDates.map((date: string) => new Date(date).toISOString().split("T")[0]);
      }

      if (apiParams.startDate) {
        apiParams.startDate = new Date(apiParams.startDate).toISOString().split("T")[0];
      }

      if (apiParams.endDate) {
        apiParams.endDate = new Date(apiParams.endDate).toISOString().split("T")[0];
      }

      // Convert pageNumber/pageSize to page/size for API compatibility
      if (params?.pageNumber !== undefined) {
        apiParams.page = params.pageNumber;
        delete apiParams.pageNumber;
      }
      if (params?.pageSize !== undefined) {
        apiParams.size = params.pageSize;
        delete apiParams.pageSize;
      }

      // Never search without a resolved operating country — the backend would
      // otherwise fall back to a default region and return the wrong inventory.
      const gateConfig: any = { headers: {} };
      this.assertMarketResolved(gateConfig);

      const response = await this.api.get("/api/v2/spaces/search", {
        params: apiParams,
      });

      logger.log("Public spaces search response:", response);
      return response;
    } catch (error) {
      logger.error("Error searching public spaces:", error);
      throw error;
    }
  }

  public async searchPublicSpacesUnauthenticated(params?: {
    addressContains?: string;
    amenityIds?: string;
    buildingAddressId?: number;
    buildingName?: string;
    categoryId?: number;
    city?: string;
    country?: string;
    duration?: string;
    endDate?: string;
    endTime?: string;
    featured?: boolean;
    filterOptionIds?: number[] | string;
    operatingTerritoryIds?: number[] | string;
    hostOrgName?: string;
    hostOrganizationId?: number;
    maxPrice?: number;
    minCapacity?: number;
    minPrice?: number;
    offset?: number;
    pageNumber?: number;
    pageSize?: number;
    paged?: boolean;
    "sort.sorted"?: boolean;
    "sort.unsorted"?: boolean;
    spaceName?: string;
    specificDate?: string | string[];
    startDate?: string;
    startTime?: string;
    status?: "PUBLISHED" | "UNPUBLISHED";
    subCategoryId?: number;
    timezone?: number;
    topSpaces?: boolean;
    unpaged?: boolean;
  }): Promise<ApiResponse<SpaceSearchResponse>> {
    try {
      logger.log("🔍 searchPublicSpacesUnauthenticated - Original params:", params);
      logger.log("📅 Date/Time params:", {
        startDate: params?.startDate,
        endDate: params?.endDate,
        startTime: params?.startTime,
        endTime: params?.endTime,
        timezone: params?.timezone,
      });

      // Process date parameters to convert to date-only format
      const apiParams: any = { ...params };

      // Convert subcategory-filter option ids to a comma-separated string (e.g. 10,11,201)
      if (apiParams.filterOptionIds && apiParams.filterOptionIds.length > 0) {
        apiParams.filterOptionIds = Array.isArray(apiParams.filterOptionIds)
          ? apiParams.filterOptionIds.join(",")
          : String(apiParams.filterOptionIds);
      } else {
        delete apiParams.filterOptionIds;
      }

      // Convert operating-territory ids to a comma-separated string (e.g. 2306104,2298890)
      if (apiParams.operatingTerritoryIds && apiParams.operatingTerritoryIds.length > 0) {
        apiParams.operatingTerritoryIds = Array.isArray(apiParams.operatingTerritoryIds)
          ? apiParams.operatingTerritoryIds.join(",")
          : String(apiParams.operatingTerritoryIds);
      } else {
        delete apiParams.operatingTerritoryIds;
      }

      // Convert dates to date-only format (YYYY-MM-DD)
      if (apiParams.specificDate) {
        const specificDates = Array.isArray(apiParams.specificDate) ? apiParams.specificDate : [apiParams.specificDate];
        apiParams.specificDate = specificDates.map((date: string) => new Date(date).toISOString().split("T")[0]);
      }

      if (apiParams.startDate) {
        apiParams.startDate = new Date(apiParams.startDate).toISOString().split("T")[0];
      }

      if (apiParams.endDate) {
        apiParams.endDate = new Date(apiParams.endDate).toISOString().split("T")[0];
      }

      // Convert pageNumber/pageSize to page/size for API compatibility
      if (params?.pageNumber !== undefined) {
        apiParams.page = params.pageNumber;
        delete apiParams.pageNumber;
      }
      if (params?.pageSize !== undefined) {
        apiParams.size = params.pageSize;
        delete apiParams.pageSize;
      }

      logger.log("🚀 Final API params being sent:", apiParams);
      logger.log("🌐 API URL will be:", `${Constants.SPACE_MANAGEMENT_API_BASE_URL}/api/v2/spaces/public-search`);

      // Create a new axios instance without auth interceptors for public endpoints
      const publicApi = axios.create({
        baseURL: Constants.SPACE_MANAGEMENT_API_BASE_URL,
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Never search without a resolved operating country — the backend would
      // otherwise fall back to a default region and return the wrong inventory.
      this.assertMarketResolved(publicApi.defaults);

      const response = await publicApi.get("/api/v2/spaces/public-search", {
        params: apiParams,
      });

      logger.log("Public spaces search (unauthenticated) response:", response);
      return response;
    } catch (error) {
      logger.error("Error searching public spaces (unauthenticated):", error);
      throw error;
    }
  }

  /**
   * Fetch the subcategory filters (with embedded options) for a subcategory.
   * Used by the search bar when the MOVE category is active.
   * GET /api/v2/subcategory-filters?subCategoryId=<id> → Filter[] (each with embedded options)
   */
  public async getSubcategoryFilters(subCategoryId: number): Promise<ApiResponse<any>> {
    return this.api.get("/api/v2/subcategory-filters", { params: { subCategoryId } });
  }

  /**
   * Fetch the operating territories for a country (MOVE category). Optionally narrow to a
   * subcategory (a territory is returned if the subCategoryId is in its subCategoryIds).
   * @RequiresAuth — uses `this.api`, which already attaches the app-key + user-metadata headers.
   * GET /api/v2/operating-territories?country=<ISO2>[&subCategoryId=<id>]
   */
  public async getOperatingTerritories(country: string, subCategoryId?: number): Promise<ApiResponse<any>> {
    // ISO-2 country codes are uppercase; normalize so a lowercase feed value still matches.
    return this.api.get("/api/v2/operating-territories", {
      params: { country: (country || "").toUpperCase(), ...(subCategoryId ? { subCategoryId } : {}) },
    });
  }

  /**
   * Fetch the countries that actually have operating territories (MOVE category).
   * @RequiresAuth — uses `this.api`. Returns [{ code, name, flag, flagUrl, currency, callingCode }].
   * GET /api/v2/operating-territories/countries
   */
  public async getOperatingTerritoryCountries(): Promise<ApiResponse<any>> {
    return this.api.get("/api/v2/operating-territories/countries");
  }

  /**
   * @deprecated Use searchBookings instead for better filtering support
   */
  public async getBookings(filters: BookingFilters = {}, timezone?: number): Promise<ApiResponse<BookingsResponse>> {
    try {
      logger.log("Getting bookings with filters:", filters);

      const params = new URLSearchParams();

      // Add filters as query parameters
      if (filters.spaceId) params.append("spaceId", filters.spaceId.toString());
      if (filters.status) params.append("status", filters.status);
      if (filters.startDate) params.append("startDate", filters.startDate);
      if (filters.endDate) params.append("endDate", filters.endDate);
      if (filters.pageNumber !== undefined) params.append("page", filters.pageNumber.toString());
      if (filters.pageSize) params.append("size", filters.pageSize.toString());
      if (filters.sortBy) params.append("sortBy", filters.sortBy);
      if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);

      // Timezone offset for context
      if (timezone !== undefined) params.append("timezone", timezone.toString());

      const response = await this.api.get(`/api/v2/bookings?${params.toString()}`);

      logger.log("Get bookings response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting bookings:", error);
      throw error;
    }
  }

  public async searchBookings(filters: BookingFilters = {}, timezone?: number): Promise<ApiResponse<BookingsResponse>> {
    try {
      logger.log("Searching bookings with filters:", filters);

      const params = new URLSearchParams();

      // Basic filters
      if (filters.userId) params.append("userId", filters.userId.toString());
      if (filters.spaceId) params.append("spaceId", filters.spaceId.toString());
      if (filters.spaceInstanceId) params.append("spaceInstanceId", filters.spaceInstanceId.toString());
      if (filters.status) params.append("status", filters.status);

      // Date/Time filters
      if (filters.startDate) params.append("startDate", filters.startDate);
      if (filters.endDate) params.append("endDate", filters.endDate);
      if (filters.startTimeFrom) params.append("startTimeFrom", filters.startTimeFrom);
      if (filters.startTimeTo) params.append("startTimeTo", filters.startTimeTo);
      if (filters.endTimeFrom) params.append("endTimeFrom", filters.endTimeFrom);
      if (filters.endTimeTo) params.append("endTimeTo", filters.endTimeTo);

      // Location filters
      if (filters.addressContains) params.append("addressContains", filters.addressContains);
      if (filters.city) params.append("city", filters.city);
      if (filters.buildingName) params.append("buildingName", filters.buildingName);

      // Space filters
      if (filters.spaceName) params.append("spaceName", filters.spaceName);
      if (filters.subCategoryId) params.append("subCategoryId", filters.subCategoryId.toString());
      if (filters.amenityIds) params.append("amenityIds", filters.amenityIds);

      // Booking owner filters
      if (filters.bookingOwnerEmail) params.append("bookingOwnerEmail", filters.bookingOwnerEmail);
      if (filters.bookingOwnerName) params.append("bookingOwnerName", filters.bookingOwnerName);

      // Host filters
      if (filters.hostOrgName) params.append("hostOrgName", filters.hostOrgName);

      // Pagination and sorting
      if (filters.page !== undefined) params.append("page", filters.page.toString());
      else if (filters.pageNumber !== undefined) params.append("page", filters.pageNumber.toString());
      if (filters.size !== undefined) params.append("size", filters.size.toString());
      else if (filters.pageSize !== undefined) params.append("size", filters.pageSize.toString());
      if (filters.offset !== undefined) params.append("offset", filters.offset.toString());
      if (filters.sort) params.append("sort", filters.sort);
      if (filters.sortBy) params.append("sortBy", filters.sortBy);
      if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);
      if (filters.paged !== undefined) params.append("paged", filters.paged.toString());
      if (filters.unpaged !== undefined) params.append("unpaged", filters.unpaged.toString());
      if (filters["sort.sorted"] !== undefined) params.append("sort.sorted", filters["sort.sorted"].toString());
      if (filters["sort.unsorted"] !== undefined) params.append("sort.unsorted", filters["sort.unsorted"].toString());

      // General search parameter
      if (filters.search) params.append("search", filters.search);

      // Timezone offset for context
      if (timezone !== undefined) params.append("timezone", timezone.toString());

      const response = await this.api.get(`/api/v2/bookings/search?${params.toString()}`);

      logger.log("Search bookings response:", response);
      return response;
    } catch (error) {
      logger.error("Error searching bookings:", error);
      throw error;
    }
  }

  /**
   * Get booking availability for a space
   * Returns booked time slots and instance info for availability checking
   * @param params - Query parameters (spaceId, spaceInstanceId, from, to)
   * @returns Response with totalInstances, instanceIds, and bookedSlots
   */
  /**
   * Public (unauthenticated) booking availability.
   *
   * Lets a signed-out visitor check which dates/times are free before choosing
   * to sign in, mirroring mobile's public availability client. Same endpoint and
   * response shape as getBookingAvailability.
   */
  public async getPublicBookingAvailability(params: {
    spaceId?: number;
    spaceInstanceId?: number;
    from?: string;
    to?: string;
  }): Promise<
    ApiResponse<{
      spaceId: number;
      totalInstances: number;
      instanceIds: number[];
      bookedSlots: Array<{ spaceInstanceId: number; startTime: string; endTime: string; status?: string }>;
    }>
  > {
    return this.getBookingAvailability(params);
  }

  public async getBookingAvailability(params: {
    spaceId?: number;
    spaceInstanceId?: number;
    from?: string;
    to?: string;
  }): Promise<
    ApiResponse<{
      spaceId: number;
      totalInstances: number;
      instanceIds: number[];
      bookedSlots: Array<{ spaceInstanceId: number; startTime: string; endTime: string; status?: string }>;
    }>
  > {
    try {
      logger.log("Getting booking availability with params:", params);

      const queryParams = new URLSearchParams();
      if (params.spaceId) queryParams.append("spaceId", params.spaceId.toString());
      if (params.spaceInstanceId) queryParams.append("spaceInstanceId", params.spaceInstanceId.toString());
      if (params.from) queryParams.append("from", params.from);
      if (params.to) queryParams.append("to", params.to);

      const response = await this.api.get(`/api/v2/bookings/availability?${queryParams.toString()}`);

      logger.log("Booking availability response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting booking availability:", error);
      throw error;
    }
  }

  public async createBooking(payload: CreateBookingPayload): Promise<ApiResponse<Booking>> {
    try {
      logger.log("Creating booking with payload:", payload);

      // All dates should already be in UTC format
      const bookingPayload = payload;

      const response = await this.api.post("/api/v2/bookings", bookingPayload);

      logger.log("Create booking response:", response);
      return response;
    } catch (error) {
      logger.error("Error creating booking:", error);
      throw error;
    }
  }

  public async createBookingPendingPayment(payload: CreateBookingPayload): Promise<ApiResponse<Booking>> {
    try {
      logger.log("Creating booking pending payment with payload:", payload);

      // All dates should already be in UTC format
      const bookingPayload = payload;

      const response = await this.api.post("/api/v2/bookings/pending-payment", bookingPayload);

      logger.log("Create booking pending payment response:", response);
      return response;
    } catch (error) {
      logger.error("Error creating booking pending payment:", error);
      throw error;
    }
  }

  public async getBookingById(id: number): Promise<ApiResponse<Booking>> {
    try {
      logger.log("Getting booking by id:", id);

      const response = await this.api.get(`/api/v2/bookings/${id}`);

      logger.log("Get booking by id response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting booking by id:", error);
      throw error;
    }
  }

  public async updateBooking(
    id: number,
    payload: UpdateBookingPayload,
    timezone?: number
  ): Promise<ApiResponse<Booking>> {
    try {
      logger.log("Updating booking with id:", id, "payload:", payload);

      // All dates should already be in UTC format (ISO strings)
      const updatePayload: UpdateBookingPayload = {
        ...payload,
      };

      // Ensure dates are in ISO format if provided
      if (payload.newStartTime) {
        updatePayload.newStartTime = new Date(payload.newStartTime).toISOString();
      }
      if (payload.newEndTime) {
        updatePayload.newEndTime = new Date(payload.newEndTime).toISOString();
      }

      // Add timezone offset if provided (as query parameter)
      const params = new URLSearchParams();
      if (timezone !== undefined) {
        params.append("timezone", timezone.toString());
      }

      const url = params.toString() ? `/api/v2/bookings/${id}?${params.toString()}` : `/api/v2/bookings/${id}`;

      const response = await this.api.patch(url, updatePayload);

      logger.log("Update booking response:", response);
      return response;
    } catch (error) {
      logger.error("Error updating booking:", error);
      throw error;
    }
  }

  public async cancelBooking(id: number): Promise<ApiResponse> {
    try {
      logger.log("Cancelling booking with id:", id);

      const response = await this.api.post(`/api/v2/bookings/${id}/cancel`);

      logger.log("Cancel booking response:", response);
      return response;
    } catch (error) {
      logger.error("Error cancelling booking:", error);
      throw error;
    }
  }

  public async checkInBooking(id: number): Promise<ApiResponse> {
    try {
      logger.log("Checking in booking with id:", id);

      const response = await this.api.post(`/api/v2/bookings/${id}/check-in`);

      logger.log("Check-in booking response:", response);
      return response;
    } catch (error) {
      logger.error("Error checking in booking:", error);
      throw error;
    }
  }

  public async checkOutBooking(id: number): Promise<ApiResponse> {
    try {
      logger.log("Checking out booking with id:", id);

      const response = await this.api.post(`/api/v2/bookings/${id}/check-out`);

      logger.log("Check-out booking response:", response);
      return response;
    } catch (error) {
      logger.error("Error checking out booking:", error);
      throw error;
    }
  }

  public async getUserBookings(
    userId: number,
    filters: Omit<BookingFilters, "userId"> = {},
    timezone?: number
  ): Promise<ApiResponse<BookingsResponse>> {
    return this.searchBookings({ ...filters, userId }, timezone);
  }

  /**
   * Get booking statistics including occupancy rates
   * @param year Optional year for statistics (defaults to current year)
   * @returns Promise with booking statistics
   */
  public async getBookingStatistics(year?: number): Promise<ApiResponse<any>> {
    try {
      const queryParams = new URLSearchParams();
      if (year) {
        queryParams.append("year", year.toString());
      }

      const response = await this.api.get(`/api/v2/bookings/statistics?${queryParams.toString()}`);

      logger.log("Booking Statistics Response:", response);
      return response;
    } catch (error) {
      logger.error("Get Booking Statistics Error:", error);
      throw error;
    }
  }

  /**
   * Get the caller organization's booking policy
   * GET /api/v2/organizations/booking-policy
   */
  public async getBookingPolicy(): Promise<ApiResponse<any>> {
    try {
      logger.log("Getting organization booking policy");

      const response = await this.api.get("/api/v2/organizations/booking-policy");

      logger.log("Get booking policy response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting booking policy:", error);
      throw error;
    }
  }

  /**
   * Set (create or update) the caller organization's minimum advance-booking notice
   * PUT /api/v2/organizations/booking-policy
   */
  public async setBookingPolicy(payload: { minAdvanceBookingHours: number }): Promise<ApiResponse<any>> {
    try {
      logger.log("Setting organization booking policy with payload:", payload);

      const response = await this.api.put("/api/v2/organizations/booking-policy", payload);

      logger.log("Set booking policy response:", response);
      return response;
    } catch (error) {
      logger.error("Error setting booking policy:", error);
      throw error;
    }
  }

  /**
   * Clear the caller organization's booking policy
   * DELETE /api/v2/organizations/booking-policy
   */
  public async clearBookingPolicy(): Promise<ApiResponse<any>> {
    try {
      logger.log("Clearing organization booking policy");

      const response = await this.api.delete("/api/v2/organizations/booking-policy");

      logger.log("Clear booking policy response:", response);
      return response;
    } catch (error) {
      logger.error("Error clearing booking policy:", error);
      throw error;
    }
  }
}

export const spaceManagementService = new SpaceManagementService();
export default spaceManagementService;
